import {
	closestCenter,
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { useThemeColors } from '../../../providers/ThemeProvider';

// Custom sensor that only activates drag when the pointer starts on a drag handle.
// This allows text selection and clicking inside draggable items while keeping
// the drag overlay properly aligned (listeners stay on the wrapper element).
class HandlePointerSensor extends PointerSensor {
	static activators = [
		{
			eventName: 'onPointerDown' as const,
			handler: ({ nativeEvent: event }: { nativeEvent: Event }) => {
				const target = event.target as HTMLElement;
				return target.closest('[data-drag-handle]') != null;
			},
		},
	];
}

interface DraggableListProps<T> {
	data: T[];
	keyExtractor: (item: T, index: number) => string;
	renderItem: (item: T, index: number, isDragging: boolean) => ReactNode;
	onReorder: (data: T[]) => void;
	className?: string;
	dragDataExtractor?: (item: T) => string;
	allowInternalReordering?: boolean;
	onDragStart?: () => void;
	onDragEnd?: () => void;
	onDrop?: (dragData: string, targetIndex: number) => void;
	listId?: string;
	acceptDropFrom?: string[];
	allowPositionalDrops?: boolean;
}

export function DraggableList<T>({
	data,
	keyExtractor,
	renderItem,
	onReorder,
	className = '',
	dragDataExtractor,
	allowInternalReordering = true,
	onDragStart,
	onDragEnd,
	onDrop,
	listId,
	acceptDropFrom = [],
	allowPositionalDrops = true,
}: DraggableListProps<T>) {
	const [activeId, setActiveId] = useState<string | null>(null);
	const [activeItem, setActiveItem] = useState<T | null>(null);
	const colors = useThemeColors();

	const sensors = useSensors(
		useSensor(HandlePointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
	);

	const items = data.map((item, index) => ({
		id: keyExtractor(item, index),
		item,
	}));

	const handleDragStart = (event: DragStartEvent) => {
		const { active } = event;
		const activeItem = items.find((item) => item.id === active.id);

		setActiveId(active.id as string);
		setActiveItem(activeItem?.item || null);
		// Force grabbing cursor on all elements during drag
		const style = document.createElement('style');
		style.id = 'draggable-list-cursor';
		style.textContent = '* { cursor: grabbing !important; }';
		document.head.appendChild(style);

		if (onDragStart) {
			onDragStart();
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (active.id !== over?.id && over && allowInternalReordering) {
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				const newData = arrayMove(data, oldIndex, newIndex);
				onReorder(newData);
			}
		}

		setActiveId(null);
		setActiveItem(null);
		const cursorStyle = document.getElementById('draggable-list-cursor');
		if (cursorStyle != null) {
			cursorStyle.remove();
		}

		if (onDragEnd) {
			onDragEnd();
		}
	};

	const itemIds = items.map((item) => item.id);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
		>
			<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
				<div className={`min-h-full pb-5 ${className}`}>
					{items.map((itemData, index) => (
						<SortableItem
							key={itemData.id}
							id={itemData.id}
							item={itemData.item}
							index={index}
							isDragging={activeId === itemData.id}
							renderItem={renderItem}
						/>
					))}
				</div>
			</SortableContext>

			<DragOverlay>
				{activeId && activeItem ? (
					<div className="opacity-80">
						{renderItem(
							activeItem,
							items.findIndex((item) => item.id === activeId),
							true,
						)}
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	);
}

interface SortableItemProps<T> {
	id: string;
	item: T;
	index: number;
	isDragging: boolean;
	renderItem: (item: T, index: number, isDragging: boolean) => ReactNode;
}

function SortableItem<T>({
	id,
	item,
	index,
	isDragging,
	renderItem,
}: SortableItemProps<T>) {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="w-full"
		>
			{renderItem(item, index, isDragging)}
		</div>
	);
}

// Draggable handle component — only this element initiates dragging.
// The HandlePointerSensor checks for [data-drag-handle] before activating.
interface DragHandleProps {
	className?: string;
}

export function DragHandle({ className = '' }: DragHandleProps) {
	const colors = useThemeColors();

	return (
		<div className={`p-1 cursor-grab ${className}`} data-drag-handle="true">
			<Icon icon={faGripVertical} size="xs" color={colors.values.textMuted} />
		</div>
	);
}
